// 📁 src/components/VisualWrapper.jsx
// vFinal_stable_noHeightConflict

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, ArrowLeft } from 'lucide-react';

const VisualWrapper = ({ children, sidebarVisible, setSidebarVisible, hideHeader = false }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const showBackArrow = pathname !== '/home';
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const isMobileLikeLayout = windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Efecto para controlar overflow del body solo en dashboard
  useEffect(() => {
    document.body.classList.add('dashboard-layout');
    return () => {
      document.body.classList.remove('dashboard-layout');
    };
  }, []);

  return (
    <>
      {/* Header Sticky - Ocupa espacio físico (oculto en modo immersive) */}
      {!hideHeader && (
      <header className="sticky top-0 left-0 right-0 bg-gradient-to-r from-[#1E2A39] to-[#0c1623] text-white px-3 md:px-4 flex items-center justify-between z-50 h-[var(--dashboard-header-height-mobile)] md:h-[var(--dashboard-header-height-desktop)] shadow-md flex-shrink-0">
        <div className="flex items-center gap-1 h-full">
          {/* Flecha atrás — solo mobile, oculta en /home */}
          {showBackArrow && (
          <button
            onClick={() => pathname === '/dashboard' ? navigate('/home') : navigate(-1)}
            className="sm:hidden p-2 rounded-full text-white hover:bg-white/10 transition-all min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Volver a la página anterior"
          >
            <ArrowLeft size={20} />
          </button>
          )}

          <Link 
            to="/" 
            className="flex items-center gap-2 md:gap-3 h-full cursor-pointer hover:opacity-80 transition-opacity"
            aria-label="Ir a página principal"
          >
            <img
              src="/assets/isotipoedutech.png"
              alt="Edutech Pulse Lab Logo"
              className="h-6 md:h-10 w-auto object-contain"
            />
            <h1 className="text-sm md:text-lg lg:text-xl font-semibold tracking-wide">
              <span className="hidden sm:inline">Edutech </span>
              <span className="font-light text-gray-300 hidden sm:inline">Pulse Lab</span>
              <span className="sm:hidden font-medium">EPL</span>
            </h1>
          </Link>
        </div>

        <button
          onClick={() => setSidebarVisible(true)}
          className="p-3 md:p-2 rounded-full text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-75 transition-all duration-150 ease-in-out max-[1180px]:block min-[1181px]:hidden min-h-[44px] min-w-[44px] flex items-center justify-center"
          title="Abrir menú"
          aria-label="Abrir menú de navegación"
        >
          <Menu size={20} />
        </button>
      </header>
      )}

      {/* Contenedor principal - Altura exacta restante. Sin header: 100dvh completo */}
      <div className={`bg-[#F9FAFB] text-gray-800 font-sans flex flex-row overflow-hidden ${
        hideHeader
          ? 'h-screen supports-[height:100dvh]:h-[100dvh] min-h-screen'
          : 'h-[calc(100vh-var(--dashboard-header-height-mobile))] md:h-[calc(100vh-var(--dashboard-header-height-desktop))] supports-[height:100dvh]:h-[calc(100dvh-var(--dashboard-header-height-mobile))] md:supports-[height:100dvh]:h-[calc(100dvh-var(--dashboard-header-height-desktop))] min-h-[calc(100dvh-var(--dashboard-header-height-mobile))] md:min-h-[calc(100dvh-var(--dashboard-header-height-desktop))]'
      }`}>
        
        <style>{`
          /* Solo para páginas de dashboard - Previene doble scroll */
          body.dashboard-layout {
            overflow: hidden !important;
            overscroll-behavior: none;
          }
        `}</style>

        {/* Contenedor flex para sidebar + main (1 columna en mobile/tablet, 2-3 en desktop) */}
        <div className={isMobileLikeLayout ? 'flex flex-col w-full h-full overflow-hidden' : 'flex flex-row w-full h-full overflow-hidden'}>
          {React.Children.map(children, child =>
            React.isValidElement(child) && typeof child.type !== 'string'
              ? React.cloneElement(child, { isMobileLikeLayout })
              : child
          )}
        </div>
      </div>
    </>
  );
};

export default VisualWrapper;
